'use client'

import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { useLocale } from '@/components/LocaleContext'
import {
  PRODUCT_CAROUSEL_CACHE_KEY,
  readProductCarouselCache,
  writeProductCarouselCache,
} from '@/lib/productCarouselCache'
import type { ProductCardProps } from './ProductCard'
import { ProductsPageCarousel } from './ProductsPageCarousel'

type CarouselApiResponse = {
  products: ProductCardProps[]
  updatedAt: number
}

export function ProductsCarouselClient({
  heading = 'PRODUCTS',
  marquee = true,
}: {
  heading?: string
  marquee?: boolean
}) {
  const { locale, translatedData } = useLocale()
  const [cachedProducts, setCachedProducts] = useState<ProductCardProps[] | null>(null)

  useEffect(() => {
    let mounted = true

    const loadCached = async () => {
      const cached = await readProductCarouselCache<ProductCardProps[]>(
        PRODUCT_CAROUSEL_CACHE_KEY,
        { allowStale: true },
      )
      if (!mounted) return
      if (cached && cached.length > 0) {
        setCachedProducts(cached)
      }
    }

    void loadCached()
    return () => {
      mounted = false
    }
  }, [])

  const fetchCarousel = async (): Promise<ProductCardProps[]> => {
    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => controller.abort(), 4500)

    try {
      const res = await fetch('/api/products/carousel', {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      })

      if (!res.ok) throw new Error('Failed to load products')

      const payload = (await res.json()) as CarouselApiResponse
      const products = Array.isArray(payload.products) ? payload.products : []

      await writeProductCarouselCache(PRODUCT_CAROUSEL_CACHE_KEY, products)
      setCachedProducts(products)
      return products
    } catch (error) {
      const cached = await readProductCarouselCache<ProductCardProps[]>(
        PRODUCT_CAROUSEL_CACHE_KEY,
        { allowStale: true },
      )
      if (cached && cached.length > 0) return cached
      throw error instanceof Error ? error : new Error('Failed to load products')
    } finally {
      window.clearTimeout(timeoutId)
    }
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['products-carousel'],
    queryFn: fetchCarousel,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  })

  const products = data ?? cachedProducts ?? []

  if (isLoading && products.length === 0) {
    return (
      <div className="py-10 text-center font-poppins text-neutral-600">
        {translatedData?.common.loading_products ?? 'Loading products...'}
      </div>
    )
  }

  if (error && products.length === 0) {
    return (
      <div className="py-10 text-center font-poppins text-red-600">
        {translatedData?.common.failed_load_products_reconnect ??
          'Failed to load products. Please reconnect and try again.'}
      </div>
    )
  }

  return (
    <ProductsPageCarousel products={products} heading={heading} locale={locale} marquee={marquee} />
  )
}
